import React, { FC } from 'react';
import { TbEye, TbLock, TbMessageCircle, TbMessages, TbUserPlus } from 'react-icons/tb';

import { Anchor, Badge, Box, Button, CloseButton, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import {
    COMPONENT_INSTANCE_MENTION_TYPE,
    componentInstanceMentionId,
} from '@diagram/hooks/use-component-instance-mentions';
import { ChatService } from '@diagram/services/ChatService';

import { CompanyProfile, DiagramChatChannel, DiagramComponentInstance } from 'models';

import { Logo } from 'components/company-profile-teaser/components/Logo';
import { ModalTitle } from 'components/modals/ModalTitle';
import { useAction } from 'hooks/use-action';
import { InternalTrackingService } from 'services/InternalTrackingService';

type Props = ContextModalProps<{
    company: CompanyProfile;
    componentInstance?: DiagramComponentInstance;
    message?: string;
    channel?: DiagramChatChannel;
}>;

const AskCompany: FC<Props> = ({ context, id, innerProps }) => {
    const { company, componentInstance, message, channel } = innerProps;

    const definition = componentInstance && ComponentInstanceHelpers.definition(componentInstance.componentType);
    const subtitle = [definition && `Supports ${definition.plural}`, company.editorSupport?.description]
        .filter(Boolean)
        .join(' · ');

    const greeting = `Hi ${company.name} team, I'm looking for a ${definition?.name} for component `;
    const closing = 'Could you help me find the right product?';
    const plainQuestion =
        message ?? `Hi ${company.name} team, could you help me find the right product for this design?`;

    const question = () => {
        if (!componentInstance || message) {
            return plainQuestion;
        }

        return {
            type: 'doc',
            content: [
                {
                    type: 'paragraph',
                    content: [
                        { type: 'text', text: greeting },
                        {
                            type: COMPONENT_INSTANCE_MENTION_TYPE,
                            attrs: {
                                id: componentInstanceMentionId(componentInstance.id),
                                label: componentInstance.designator,
                            },
                        },
                        { type: 'text', text: ' in this design.' },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [{ type: 'text', text: closing }],
                },
            ],
        };
    };

    const [start, starting, , , startError] = useAction(async () => {
        if (channel) {
            InternalTrackingService.track(
                'diagram.chat.invite-company.confirm',
                { channel: channel.name, company: company.name },
                { notifySlack: true },
            );

            await ChatService.inviteCompany(channel, company);
            await ChatService.refreshChannels(channel.project);
        } else {
            await ChatService.startCompanyChannel(company, question());
        }

        context.closeModal(id);
    });

    const close = () => {
        if (!starting) {
            context.closeModal(id);
        }
    };

    const access = [
        {
            icon: <TbMessages size={16} />,
            color: 'green',
            text: channel ? `Joins #${channel.name}` : 'Joins a new chat about this design',
        },
        {
            icon: <TbEye size={16} />,
            color: 'orange',
            text: 'Gets access to this project',
            note: 'They see your entire design, including components from other companies.',
        },
        {
            icon: <TbLock size={16} />,
            color: 'red',
            text: "Doesn't see your other chats. You can remove them at any time.",
        },
    ];

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group gap="md" wrap="nowrap">
                    <Logo company={company} size={56} withBorder />
                    <Box miw={0}>
                        <ModalTitle align="left">
                            {channel ? `Add ${company.name} to #${channel.name}` : `Ask ${company.name}`}
                        </ModalTitle>
                        {subtitle && (
                            <Text fz="sm" c="dimmed" mt={2}>
                                {subtitle}
                            </Text>
                        )}
                    </Box>
                </Group>
                <CloseButton onClick={close} />
            </Group>

            {!channel && (
                <Box>
                    <Text fz="xs" fw={600} c="dimmed" mb={6}>
                        Your first message
                    </Text>
                    <Paper radius="md" p="sm" bg="gray.0">
                        {componentInstance && !message ? (
                            <Text fz="sm">
                                {greeting}
                                <Anchor
                                    inherit
                                    c="blue"
                                    fw={500}
                                    component="span"
                                    style={{
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    #{componentInstance.designator}
                                </Anchor>{' '}
                                in this design. {closing}
                            </Text>
                        ) : (
                            <Text fz="sm">{plainQuestion}</Text>
                        )}
                    </Paper>
                    <Text fz="xs" c="dimmed" mt={6}>
                        You can edit the message before sending.
                    </Text>
                </Box>
            )}

            <Stack gap={8}>
                {access.map(({ icon, color, text, note }) => (
                    <Group gap="sm" wrap="nowrap" align={note ? 'flex-start' : 'center'} key={text}>
                        <ThemeIcon variant="light" color={color} size={28} radius="md">
                            {icon}
                        </ThemeIcon>
                        <Box>
                            <Text fz="sm" c="gray.7">
                                {text}
                            </Text>
                            {note && (
                                <Text fz="xs" c="orange.8">
                                    {note}
                                </Text>
                            )}
                        </Box>
                    </Group>
                ))}
            </Stack>

            {startError && (
                <Text fz="sm" c="red">
                    {channel
                        ? `Could not add ${company.name} to the chat.`
                        : `Could not start a chat with ${company.name}.`}{' '}
                    Please try again.
                </Text>
            )}

            <Group justify="flex-end" gap="xs">
                <Button variant="default" onClick={close}>
                    Cancel
                </Button>
                <Button
                    leftSection={channel ? <TbUserPlus size={16} /> : <TbMessageCircle size={16} />}
                    onClick={() => start().catch(() => null)}
                    loading={starting}
                >
                    {channel ? 'Add to chat' : 'Start chat'}
                </Button>
            </Group>
        </Stack>
    );
};

export { AskCompany };
